import { useEffect, useMemo, useState } from "react";
import {
  advisoryApi,
  type Composition,
  type CustomProduct,
  type Portfolio,
  type Product,
  type RiskCurrent,
  type RiskOptions,
} from "../advisoryApi";
import { CompositionView, type Holding } from "../charts";
import { i18n, money, reasonText, toNumber } from "../format";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface PortfolioData {
  portfolio: Portfolio;
  composition: Composition | null;
  risk: RiskCurrent | null;
}
interface RiskData {
  options: RiskOptions;
  current: RiskCurrent | null;
}

type Suitability = { ok: boolean; text: string };

const DEFAULT_ADD = 10000;

/**
 * Custom portfolio (`custom_portfolio`) — QP's custom-portfolio-builder: the
 * holdings table (amount, derived weight), a product picker over the flow's
 * collection with a suitability column, and the risk-bound panel. QP's
 * deviation decision for an out-of-bounds mix is web-only, so here the advisor
 * adjusts the mix until it sits inside the bounds.
 */
export function CustomPortfolioStep({ sessionId, addr, steps, readOnly, onSaved, currency }: StepProps) {
  const pAddr = { goalId: addr.goalId, portfolioId: addr.portfolioId };
  const load = useStepLoad<PortfolioData>(sessionId, "portfolio-input", pAddr);
  const risk = useStepLoad<RiskData>(sessionId, "risk-assessment", { goalId: addr.goalId });
  const saver = useSaver(onSaved);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<Product[] | null>(null);
  const [productsErr, setProductsErr] = useState<string | null>(null);
  const [addAmount, setAddAmount] = useState(String(DEFAULT_ADD));
  const [edits, setEdits] = useState<Record<string, string>>({});

  // Debounced product search over the flow's collection.
  useEffect(() => {
    let stale = false;
    const t = window.setTimeout(() => {
      advisoryApi
        .products(sessionId, search.trim() || undefined)
        .then((d) => {
          if (stale) return;
          setProducts(d.data);
          setProductsErr(null);
        })
        .catch((e) => !stale && setProductsErr((e as Error).message));
    }, 300);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [sessionId, search]);

  const holdings: CustomProduct[] = useMemo(() => {
    const p = load.data?.portfolio as (Portfolio & { goalPortfolioCustomProducts?: CustomProduct[] }) | undefined;
    return p?.customProducts?.length ? p.customProducts : (p?.goalPortfolioCustomProducts ?? []);
  }, [load.data]);

  const positionsById = useMemo(() => {
    const m = new Map<string, { name: string; isin: string | null; assetClass: string | null }>();
    for (const pos of load.data?.composition?.positions ?? [])
      if (pos.productId) m.set(pos.productId, { name: pos.name, isin: pos.isin, assetClass: pos.assetClassName ?? null });
    return m;
  }, [load.data]);

  const suitability = useMemo(() => {
    const opts = risk.data?.options;
    const cur = risk.data?.current;
    const order = (list: { id: string; order: number }[] | undefined, id: string | null | undefined) =>
      id ? (list?.find((x) => x.id === id)?.order ?? null) : null;
    const goalH = order(opts?.timeHorizons, cur?.timeHorizonId);
    const goalL = order(opts?.riskLevels, cur?.riskLevelId);
    const score = cur?.result?.score ?? null;
    return (p: Product): Suitability => {
      const minH = order(opts?.timeHorizons, p.minTimeHorizonId);
      const minL = order(opts?.riskLevels, p.minRiskLevelId);
      if (minL !== null && goalL !== null && goalL < minL) return { ok: false, text: "Requires higher risk tolerance" };
      if (p.minRiskScore != null && score !== null && score < p.minRiskScore)
        return { ok: false, text: `Risk score too low (min ${p.minRiskScore})` };
      if (minH !== null && goalH !== null && goalH < minH) return { ok: false, text: "Requires a longer horizon" };
      return { ok: true, text: "Suitable" };
    };
  }, [risk.data]);

  if (!addr.goalId || !addr.portfolioId) return <p className="muted">This page needs a portfolio.</p>;
  if (load.loading && !load.data) return <p className="muted">Loading the holdings…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const openStep = steps.find((s) => s.status === "open");
  const breach = openStep?.reason === "risk_bound_breach";
  const score = risk.data?.current?.result?.score ?? null;
  const amounts = holdings.map((h) => toNumber(edits[h.id] ?? h.amount) ?? 0);
  const total = amounts.reduce((a, b) => a + b, 0);
  const held = new Set(holdings.map((h) => h.productId));
  const chartHoldings: Holding[] = holdings.map((h, i) => {
    const info = positionsById.get(h.productId);
    return {
      key: h.id,
      name: h.product?.nameI18n ? i18n(h.product.nameI18n) : (info?.name ?? h.productId),
      isin: h.product?.isin ?? info?.isin ?? null,
      assetClass: info?.assetClass ?? null,
      weight: total > 0 ? ((amounts[i] ?? 0) / total) * 100 : 0,
      amount: amounts[i] ?? 0,
    };
  });

  const refresh = () => {
    load.reload();
    setEdits({});
  };

  async function add(p: Product) {
    const amount = toNumber(addAmount);
    if (!amount || amount <= 0) return saver.setError("Enter an amount above zero to add a product.");
    const r = await saver.run(() => advisoryApi.addProduct(sessionId, addr.goalId!, addr.portfolioId!, { productId: p.id, amount }));
    if (r) refresh();
  }
  async function updateAmount(h: CustomProduct) {
    const amount = toNumber(edits[h.id]);
    if (amount === null || amount < 0) return saver.setError("Amounts must be zero or more.");
    const r = await saver.run(() => advisoryApi.patchProduct(sessionId, addr.goalId!, addr.portfolioId!, h.id, { amount }));
    if (r) refresh();
  }
  async function remove(h: CustomProduct) {
    const r = await saver.run(() => advisoryApi.deleteProduct(sessionId, addr.goalId!, addr.portfolioId!, h.id));
    if (r) refresh();
  }

  return (
    <section className="adv-step-card">
      <ul className="check-list">
        {holdings.length === 0 ? (
          <li className="check-row adv-check-open">
            <span className="check-icon" />
            <span>Add products to run the risk-bound check.</span>
          </li>
        ) : breach ? (
          <li className="check-row check-warn">
            <span className="check-icon">!</span>
            <span>
              <strong>Outside the risk bounds{score != null ? ` for risk score ${score}` : ""}.</strong> Adjust the mix — a larger
              share of bonds and money-market funds lowers the risk. A deviation from the bounds can only be justified in
              Quantfolio, so from here the advice can be submitted once the holdings sit inside them.
            </span>
          </li>
        ) : openStep ? (
          <li className="check-row adv-check-open">
            <span className="check-icon" />
            <span>{reasonText(openStep.reason) ?? "Not complete yet."}</span>
          </li>
        ) : (
          <li className="check-row check-pass">
            <span className="check-icon">✓</span>
            <span>Within the risk bounds{score != null ? ` for risk score ${score}` : ""}.</span>
          </li>
        )}
      </ul>

      <div className="adv-subsection">
        {holdings.length === 0 ? (
          <>
            <span className="adv-label">Holdings</span>
            <p className="muted">No products yet — pick them from the fund list below.</p>
          </>
        ) : (
          <CompositionView
            title="Holdings"
            currency={currency}
            holdings={chartHoldings}
            chartNote={!readOnly ? <p className="muted adv-composition-note">Switch to Table to change amounts or remove a fund.</p> : null}
            table={
          <table className="table adv-table-compact">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Amount ({currency})</th>
                <th className="num">Weight</th>
                {!readOnly && <th />}
              </tr>
            </thead>
            <tbody>
              {holdings.map((h, i) => {
                const info = positionsById.get(h.productId);
                const name = h.product?.nameI18n ? i18n(h.product.nameI18n) : (info?.name ?? h.productId);
                const isin = h.product?.isin ?? info?.isin ?? null;
                const editing = edits[h.id] !== undefined && toNumber(edits[h.id]) !== toNumber(h.amount);
                return (
                  <tr key={h.id}>
                    <td className="strong">
                      {name}
                      {isin && <div className="muted adv-isin">{isin}</div>}
                    </td>
                    <td className="num">
                      {readOnly ? (
                        money(h.amount, "")
                      ) : (
                        <input
                          className="adv-amount-input"
                          type="number"
                          min={0}
                          step={1000}
                          value={edits[h.id] ?? String(toNumber(h.amount) ?? "")}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [h.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && editing && updateAmount(h)}
                        />
                      )}
                    </td>
                    <td className="num">{total > 0 ? `${(((amounts[i] ?? 0) / total) * 100).toFixed(1)} %` : "—"}</td>
                    {!readOnly && (
                      <td>
                        <div className="cell-actions">{editing && (
                          <button type="button" className="link-action" onClick={() => updateAmount(h)} disabled={saver.saving}>
                            Update
                          </button>
                        )}
                        <button type="button" className="link-action danger" onClick={() => remove(h)} disabled={saver.saving}>
                          Remove
                        </button>
                      </div></td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{money(total, currency)}</td>
                <td className="num">100 %</td>
                {!readOnly && <td />}
              </tr>
            </tfoot>
          </table>
            }
          />
        )}
        {saver.error && <div className="banner-error">{saver.error}</div>}
      </div>

      {!readOnly && (
        <div className="adv-subsection">
          <span className="adv-label">Add from the fund list</span>
          <div className="adv-picker-bar">
            <input
              type="search"
              className="search"
              placeholder="Search by fund name or ISIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="adv-inline-field">
              Amount
              <input type="number" min={0} step={1000} value={addAmount} onChange={(e) => setAddAmount(e.target.value)} />
            </label>
          </div>
          {productsErr ? (
            <div className="banner-error">{productsErr}</div>
          ) : products === null ? (
            <p className="muted">Loading the fund list…</p>
          ) : products.length === 0 ? (
            <p className="muted">No products match “{search}”.</p>
          ) : (
            <div className="adv-picker-scroll">
              <table className="table adv-table-compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Suitability</th>
                    <th className="num">Cost</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const s = suitability(p);
                    const cost = (p.costValues ?? []).reduce((a, c) => a + (toNumber(c.value) ?? 0), 0);
                    return (
                      <tr key={p.id}>
                        <td className="strong">
                          {i18n(p.nameI18n)}
                          {p.isin && <div className="muted adv-isin">{p.isin}</div>}
                        </td>
                        <td>
                          <span className={s.ok ? "adv-suit ok" : "adv-suit no"}>{s.text}</span>
                        </td>
                        <td className="num">{cost ? `${cost.toFixed(2)} %` : "—"}</td>
                        <td>
                          <div className="cell-actions">{held.has(p.id) ? (
                            <span className="muted">Added</span>
                          ) : (
                            <button type="button" className="link-action" onClick={() => add(p)} disabled={saver.saving}>
                              Add
                            </button>
                          )}
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
